import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FieldType } from '@ngx-formly/core';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { GeoNamesService } from '../../request/service/geonames.service';
import { GeonameLocation } from '../../../models/geoname';
import { TranslateService } from '@ngx-translate/core';
import { NgSelectComponent } from '@ng-select/ng-select';

@Component({
  selector: 'formly-country-autocomplete',
  template: `
    <div class="mb-3">
      <label class="form-label" *ngIf="to.label">{{ to.label }}</label>
      <ng-select
        #ngSelect
        [items]="(filteredCountries$ | async) ?? []"
        bindLabel="displayName"
        [formControl]="control"
        [placeholder]="to.placeholder || ('Select country' | translate)"
        [searchable]="true"
        [clearable]="false"
        [typeahead]="searchInput$"
        [loading]="loading"
        [minTermLength]="3"
        [appendTo]="'body'"
        [dropdownPosition]="'auto'"
        [addTag]="false"
        [hideSelected]="false"
        [clearSearchOnAdd]="false"
        [class.is-invalid]="showError"
        (blur)="onBlur()"
        (search)="onSearch($event)"
        (change)="onChange($event)"
        (focus)="onFocus()"
      >
        <ng-template ng-notfound-tmp>
          <div class="ng-option disabled">
            {{ 'No results' | translate }}
          </div>
        </ng-template>
      </ng-select>
      <div class="invalid-feedback d-block" *ngIf="showError">
        {{ errorMessage }}
      </div>
    </div>
  `,
  styles: [
    `
      :host ::ng-deep .ng-select .ng-select-container {
        min-height: 38px;
      }

      :host ::ng-deep .ng-select.is-invalid .ng-select-container {
        border-color: var(--bs-danger) !important;
        box-shadow: 0 0 0 0.25rem rgba(var(--bs-danger-rgb), 0.25) !important;
        background-color: var(--bs-body-bg) !important;
      }

      :host ::ng-deep .ng-select.is-invalid .ng-input input {
        border: none !important;
        box-shadow: none !important;
        color: var(--bs-danger) !important;
      }

      :host ::ng-deep .ng-select.ng-select-disabled .ng-select-container {
        background-color: var(--bs-secondary-bg) !important;
      }
    `,
  ],
  standalone: false,
})
export class FormlyCountryAutocompleteComponent
  extends FieldType
  implements OnInit, OnDestroy {
  @ViewChild('ngSelect') ngSelect!: NgSelectComponent;
  allCountries: GeonameLocation[] = [];
  filteredCountries$: Observable<GeonameLocation[]> = of([]);
  searchInput$ = new Subject<string>();
  loading = false;
  private lastSearchTerm = '';
  private valueChangesSub?: Subscription;
  private langChangeSub?: Subscription;

  constructor(
    private geoNames: GeoNamesService,
    private translateService: TranslateService
  ) {
    super();
  }

  get control() {
    return this.formControl as any;
  }

  override get showError() {
    return (
      this.formControl.invalid &&
      (this.formControl.touched || this.formControl.dirty)
    );
  }

  get errorMessage() {
    if (!this.formControl.errors) return null;
    const errorKey = Object.keys(this.formControl.errors)[0];
    return (
      this.field.validation?.messages?.[errorKey] ||
      this.translateService.instant('This field is required.')
    );
  }

  /**
   * Risolve il nome del paese tradotto nella lingua di navigazione usando le chiavi
   * i18n `country.<ISO2>` (generate da i18n-iso-countries, vedi scripts/generate-country-i18n.cjs).
   * Ripiega sul `name` restituito dall'API GeoNames se la chiave non è mappata o l'i18n
   * non è ancora caricato.
   */
  private resolveDisplayName(country: GeonameLocation): string {
    if (!country.countryCode) {
      return country.name;
    }
    let code = country.countryCode.toUpperCase();
    if (code === 'EN') {
      code = 'GB'; // 'en' è usato come codice bandiera per il Regno Unito
    }
    const key = `country.${code}`;
    const translated = this.translateService.instant(key);
    return translated && translated !== key ? translated : country.name;
  }

  /** Applica/aggiorna la `displayName` tradotta a tutte le opzioni e al valore selezionato. */
  private applyDisplayNames() {
    this.allCountries.forEach((c) => (c.displayName = this.resolveDisplayName(c)));
    const currentValue = this.formControl.value;
    if (currentValue && typeof currentValue === 'object') {
      currentValue.displayName = this.resolveDisplayName(currentValue);
    }
  }

  ngOnInit() {
    this.geoNames.getCountries().subscribe((countries: GeonameLocation[]) => {
      this.allCountries = countries;

      const currentValue = this.formControl.value;
      if (currentValue && typeof currentValue === 'object' && currentValue.name) {
        const exists = this.allCountries.find(
          (c) => c.countryCode === currentValue.countryCode ||
            c.geonameId === currentValue.geonameId
        );

        if (!exists) {
          this.allCountries.unshift(currentValue);
        }
      }

      this.applyDisplayNames();
      this.filteredCountries$ = of(this.allCountries);
    });

    // Riallinea le etichette tradotte al cambio lingua (come la pipe `translate` impura).
    this.langChangeSub = this.translateService.onLangChange.subscribe(() => {
      this.applyDisplayNames();
      this.filteredCountries$ = of(this.allCountries);
    });

    this.filteredCountries$ = this.searchInput$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      tap(() => (this.loading = true)),
      switchMap((term) => {
        this.lastSearchTerm = term || '';

        if (!term || term.length === 0) {
          const currentValue = this.formControl.value;
          const currentLabel = currentValue?.displayName || currentValue?.name;
          if (currentLabel) {
            const filtered = this.allCountries.filter((c) =>
              (c.displayName || c.name).toLowerCase().includes(currentLabel.toLowerCase())
            );
            return of(filtered.length > 0 ? filtered : this.allCountries);
          }
          return of(this.allCountries);
        }

        const filtered = this.allCountries.filter((c) =>
          (c.displayName || c.name).toLowerCase().includes(term.toLowerCase())
        );
        return of(filtered);
      }),
      tap(() => (this.loading = false))
    );

    if (this.formControl.value) {
      const initial = this.formControl.value;
      if (typeof initial === 'string') {
        const match = this.allCountries.find(
          (c) => c.name.toLowerCase() === initial.toLowerCase() ||
            (c.displayName || '').toLowerCase() === initial.toLowerCase()
        );
        if (match) {
          this.formControl.setValue(match);
        }
      }
    }
  }

  onSearch(event: { term: string; items: any[] }) {
    this.lastSearchTerm = event.term || '';

    if (!event.term || event.term.trim() === '') {
      if (this.formControl.value) {
        this.formControl.setValue(null);
      }
      return;
    }

    if (event.term && event.items.length > 0) {
      const exactMatch = event.items.find(
        (item) =>
          (item.displayName || item.name).toLowerCase() === event.term.toLowerCase() ||
          item.name.toLowerCase() === event.term.toLowerCase()
      );

      if (exactMatch && this.formControl.value?.name !== exactMatch.name) {
        setTimeout(() => {
          this.formControl.setValue(exactMatch);
        }, 0);
      } else if (!exactMatch && this.formControl.value) {
        this.formControl.setValue(null);
      }
    }
  }

  onChange(value: GeonameLocation | null) {
    if (value) {
      this.lastSearchTerm = value.displayName || value.name;
    }
  }

  onFocus() {
    setTimeout(() => {
      const label = this.formControl.value?.displayName || this.formControl.value?.name;
      if (label) {
        this.ngSelect.filter(label);
        this.searchInput$.next(label);
      }
    }, 0);
  }

  onBlur() {
    this.formControl.markAsTouched();

    if (this.ngSelect?.searchTerm && !this.formControl.value) {
      setTimeout(() => {
        this.ngSelect.filter('');
      }, 100);
    }
  }

  ngOnDestroy() {
    if (this.valueChangesSub) {
      this.valueChangesSub.unsubscribe();
    }
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
    this.searchInput$.complete();
  }
}